/**
 * ChatInput — message input with file picker, send button, and interrupt control.
 *
 * File picker uses native OS dialogs via the backend: zenity/kdialog (Linux),
 * osascript (macOS), PowerShell (Windows). Falls back to a hidden HTML <input>
 * on platforms without a native dialog (ChromeOS), uploading via /api/files/drop.
 */

import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { authedUpload, nativeDialogAvailable, probeNativeDialog } from "@/api/client";
import { baseName, importPickedPaths } from "@/api/files";
import { useToastStore } from "@/components/common/Toast";
import { useChatStore } from "@/stores/chatStore";
import type { AttachedFile } from "@/stores/chatStore";
import { RecapBar } from "./RecapBar";
import { useShortlistStore } from "@/stores/shortlistStore";
import { useDictation } from "@/hooks/useDictation";
import { fetchAudioStatus, type DictationEndContext } from "@/api/audio";
import { DictationSetupModal } from "./DictationSetupModal";
import { DictationOverlay } from "./DictationOverlay";
import { EnvironmentSelector } from "./EnvironmentSelector";
import { RunningTasksChip } from "./RunningTasksChip";
import { ModelSelector } from "./ModelSelector";
import { HScroll } from "@/components/common/HScroll";
import { ComposerPlusMenu } from "./ComposerPlusMenu";
import { TokenCounter } from "./TokenCounter";
import { CostCounter } from "./CostCounter";
import { SlashCommandMenu } from "./SlashCommandMenu";
import {
  filterSlashCommands,
  getCachedSlashCommands,
  getSlashCommands,
  tokenizeSlashCommands,
  type SlashCommand,
} from "./slashCommands";

/** Number of recent chat turns to send to the dictation refiner as
 *  context (for names, casing, topic). Kept small — the backend caps too. */
const DICTATION_CONTEXT_TURNS = 4;

/**
 * Pull the plain text out of a message's serialized content blocks. Only
 * ``text`` blocks contribute; tool calls, artifacts, etc. are skipped.
 * Used to build the lightweight conversation context sent to the dictation
 * refiner. Never throws — malformed JSON yields "".
 */
function extractMessageText(contentJson: string): string {
  try {
    const blocks = JSON.parse(contentJson);
    if (!Array.isArray(blocks)) return "";
    return blocks
      .filter((b) => b && b.type === "text" && typeof b.text === "string")
      .map((b) => b.text as string)
      .join(" ")
      .trim();
  } catch {
    return "";
  }
}

// pywebview exposes window.pywebview when running in native window
declare global {
  interface Window {
    pywebview?: {
      api?: Record<string, unknown>;
      token?: string;
    };
  }
}

export function ChatInput() {
  const [text, setText] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Slash-command palette state. The menu opens when the message begins
  // with "/" (Claude Code only triggers on a leading slash). `slashQuery`
  // is the text after the slash and drives the filter.
  const [slashOpen, setSlashOpen] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);

  // Catalog is fetched from the backend (which probes the live Claude
  // Code CLI install via the SDK), not hardcoded. `catalog` starts at
  // whatever was cached by a previous mount so re-renders don't flash
  // an empty list while the request is in flight.
  const [catalog, setCatalog] = useState<SlashCommand[]>(() =>
    getCachedSlashCommands(),
  );
  const [catalogLoading, setCatalogLoading] = useState(catalog.length === 0);
  const [catalogError, setCatalogError] = useState<string | null>(null);

  // Learn OS-dialog availability before the first attach click (ChromeOS fix).
  useEffect(() => {
    probeNativeDialog();
  }, []);

  useEffect(() => {
    let cancelled = false;
    getSlashCommands()
      .then((cmds) => {
        if (cancelled) return;
        setCatalog(cmds);
        setCatalogError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCatalogError(err instanceof Error ? err.message : "fetch failed");
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const slashQuery = useMemo(() => {
    if (!text.startsWith("/")) return null;
    // Only treat as a slash query if there's no whitespace yet — once the
    // user has typed an argument (e.g. "/loop 5m"), close the palette.
    const firstSpace = text.search(/\s/);
    if (firstSpace !== -1) return null;
    return text.slice(1);
  }, [text]);
  const filteredCommands = useMemo<SlashCommand[]>(
    () => (slashQuery === null ? [] : filterSlashCommands(slashQuery, catalog)),
    [slashQuery, catalog],
  );

  // Open / close the menu in lockstep with the query, and reset the
  // cursor so it always lands on the first match.
  useEffect(() => {
    if (slashQuery === null) {
      setSlashOpen(false);
      return;
    }
    setSlashOpen(true);
    setSlashIndex(0);
  }, [slashQuery]);

  // Tokenised view of the textarea content, used by the overlay to render
  // recognised `/commands` in jove purple. Re-runs when the catalog loads
  // so freshly-discovered commands light up immediately.
  const tokens = useMemo(
    () => tokenizeSlashCommands(text, catalog),
    [text, catalog],
  );

  const sendMessage = useChatStore((s) => s.sendMessage);
  const interrupt = useChatStore((s) => s.interrupt);
  const isAgentRunning = useChatStore((s) => s.isAgentRunning);
  const convId = useChatStore((s) => s.activeConversationId);
  const queuedCount = useChatStore((s) => convId ? s.queuedMessages[convId]?.length ?? 0 : 0);
  const clearQueuedMessage = useChatStore((s) => s.clearQueuedMessage);
  const hasMessages = useChatStore((s) => s.messages.length > 0);
  const artifacts = useChatStore((s) => s.artifacts);
  const rightPanelOpen = useChatStore((s) => s.rightPanelOpen);
  const openRightPanel = useChatStore((s) => s.openRightPanel);
  const attachedFiles = useChatStore((s) => s.attachedFiles);
  const addFiles = useChatStore((s) => s.addFiles);
  const removeFile = useChatStore((s) => s.removeFile);
  const clearFiles = useChatStore((s) => s.clearFiles);
  const liveData = useShortlistStore((s) => s.liveData);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Dictation. The chosen mic device is persisted in localStorage so the
  // user only sees the setup modal once unless they explicitly reopen it
  // via the chevron next to the mic button.
  const [micDeviceId, setMicDeviceId] = useState<string | null>(() =>
    typeof window !== "undefined" ? localStorage.getItem("dictation.deviceId") : null,
  );
  const [setupOpen, setSetupOpen] = useState(false);
  // Tracks whether the recognizer is installed on the backend — null
  // until we've made the first status check, then true/false.
  const [modelReady, setModelReady] = useState<boolean | null>(null);

  // Whether the last decoded utterance exceeded the backend's
  // MAX_UTTERANCE_SECONDS cap — shown as a one-line hint so the user
  // knows the tail of their speech was clipped.
  const [dictationTruncated, setDictationTruncated] = useState(false);

  const dictation = useDictation({
    onFinal: (value, truncated) => {
      // ``value`` is the FULL new box contents the backend produced — it
      // already integrated the new dictation into the draft we sent at
      // stop() time (and applied any spoken edit commands). Replace the
      // box with it rather than appending.
      setDictationTruncated(truncated);
      setText(value);
    },
    deviceId: micDeviceId ?? undefined,
  });
  const isDictating =
    dictation.status === "recording" ||
    dictation.status === "requesting" ||
    dictation.status === "finalizing";
  const isFinalizing = dictation.status === "finalizing";

  // Probe the model state once on mount so the mic button can decide
  // whether to start dictation directly or open the setup modal.
  useEffect(() => {
    let cancelled = false;
    fetchAudioStatus()
      .then((s) => {
        if (!cancelled) setModelReady(s.asr.installed);
      })
      .catch(() => {
        if (!cancelled) setModelReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persistDeviceId = useCallback((id: string | null) => {
    setMicDeviceId(id);
    if (id) localStorage.setItem("dictation.deviceId", id);
    else localStorage.removeItem("dictation.deviceId");
  }, []);

  // Snapshot the draft + last few chat turns to ship with the dictation
  // so the refiner can integrate the new speech into what's already typed
  // and resolve names/casing from context. Read messages via getState() so
  // we don't add a high-churn subscription to this component.
  const buildDictationContext = useCallback((): DictationEndContext => {
    const msgs = useChatStore.getState().messages;
    const recent = msgs
      .filter((m) => m.role === "user" || m.role === "assistant")
      .slice(-DICTATION_CONTEXT_TURNS)
      .map((m) => ({ role: m.role, text: extractMessageText(m.content_json) }))
      .filter((m) => m.text.length > 0);
    return { draft: text, recent };
  }, [text]);

  const handleMicClick = useCallback(() => {
    if (isDictating) {
      // Already recording (or finalizing): the mic button doubles as
      // "stop and transcribe". Cancel lives on the overlay's ✕.
      if (dictation.status === "recording") {
        dictation.stop(buildDictationContext());
      }
      return;
    }
    // First-time setup: model not installed yet → open the modal so the
    // user can trigger the download + pick a device before recording.
    if (modelReady === false) {
      setSetupOpen(true);
      return;
    }
    setDictationTruncated(false);
    void dictation.start();
  }, [isDictating, dictation, modelReady, buildDictationContext]);

  const handleDictationCancel = useCallback(() => {
    dictation.cancel();
    setDictationTruncated(false);
  }, [dictation]);

  const handleChevronClick = useCallback(() => {
    setSetupOpen(true);
  }, []);

  const handleSetupDone = useCallback(() => {
    setSetupOpen(false);
    // Re-probe so the next mic click goes straight to recording.
    fetchAudioStatus()
      .then((s) => setModelReady(s.asr.installed))
      .catch(() => {});
  }, []);

  // Auto-resize textarea. When empty, stay at the single-row intrinsic height
  // and DON'T grow to `scrollHeight` — some mobile engines fold a long, wrapped
  // placeholder into an empty textarea's scrollHeight, which otherwise inflates
  // the composer to 2+ rows before the user has typed anything.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    if (text) el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [text]);

  /**
   * Upload files via /api/files/drop (same as drag-and-drop) and return
   * AttachedFile objects with real filesystem paths.
   */
  const uploadFilesViaDropEndpoint = useCallback(async (files: File[]): Promise<AttachedFile[]> => {
    const results: AttachedFile[] = [];
    for (const file of files) {
      try {
        const form = new FormData();
        // Preserve a meaningful filename even when the File has none (e.g.
        // clipboard images on some browsers arrive as ``""``).
        form.append("file", file, file.name || "upload");
        // Route the upload into the active conversation's workspace so it's
        // bounded to this chat, replicates cross-device, and is seeded into the
        // sandbox. No id (home screen, pre-conversation) → the legacy shared dir.
        const convId = useChatStore.getState().activeConversationId;
        const dropUrl = convId
          ? `/api/files/drop?conversation_id=${encodeURIComponent(convId)}`
          : "/api/files/drop";
        const res = await authedUpload(dropUrl, form);
        if (res.ok) {
          const data = await res.json();
          if (data.path) {
            results.push({ name: file.name, path: data.path });
          }
        }
      } catch (err) {
        console.error("File upload failed for", file.name, err);
      }
    }
    return results;
  }, []);

  /**
   * Copy natively-picked files into the conversation workspace, and attach the
   * COPIES — never the ``~/Downloads`` path the OS dialog handed back, which
   * resolves on this machine only (see ``importPickedPaths``).
   *
   * A file that can't be copied is NOT attached: telling someone their document
   * is attached when it will vanish on every other device is the failure this
   * path exists to remove. They get a specific reason instead.
   */
  const importPickedFiles = useCallback(
    async (paths: string[]): Promise<AttachedFile[]> => {
      const convId = useChatStore.getState().activeConversationId;
      const { imported, failed } = await importPickedPaths(paths, convId);
      for (const [src, reason] of Object.entries(failed)) {
        useToastStore
          .getState()
          .add(`Couldn't attach ${baseName(src)} — ${reason}`, "error");
      }
      return Object.entries(imported).map(([src, copied]) => ({
        name: baseName(src),
        path: copied,
      }));
    },
    [],
  );

  /** Handle files selected via the hidden <input type="file"> fallback. */
  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (!fileList || fileList.length === 0) return;
    const files = await uploadFilesViaDropEndpoint(Array.from(fileList));
    if (files.length > 0) addFiles(files);
    // Reset so the same file can be re-selected
    e.target.value = "";
  }, [uploadFilesViaDropEndpoint, addFiles]);

  /**
   * Paste handler — extracts image files from the clipboard and attaches them
   * via the same `/api/files/drop` flow used by drag-and-drop. Works across
   * Chromium, WebKit, and Gecko (and therefore pywebview on Linux/macOS/Windows)
   * because the `paste` event + `clipboardData.items` are standardised.
   *
   * We only call ``preventDefault`` when at least one image is found, so plain
   * text paste keeps working untouched.
   */
  const handlePaste = useCallback(async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items || items.length === 0) return;

    const images: File[] = [];
    for (const item of Array.from(items)) {
      if (item.kind !== "file") continue;
      if (!item.type.startsWith("image/")) continue;
      const file = item.getAsFile();
      if (!file) continue;
      // Clipboard images typically arrive with a generic or empty filename
      // ("image.png" on Chrome, ``""`` on some WebKit builds). Synthesize a
      // timestamped name so each paste produces a unique, identifiable file
      // and the agent can recognise it by extension.
      const ext = (item.type.split("/")[1] || "png").split("+")[0];
      const stamp = new Date()
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "_")
        .replace(/Z$/, "");
      const name = `pasted-image-${stamp}.${ext}`;
      images.push(new File([file], name, { type: item.type }));
    }

    if (images.length === 0) return;
    e.preventDefault();
    const uploaded = await uploadFilesViaDropEndpoint(images);
    if (uploaded.length > 0) addFiles(uploaded);
  }, [uploadFilesViaDropEndpoint, addFiles]);

  /**
   * Open a native file dialog and return real filesystem paths.
   *
   * Backend supports Linux (zenity/kdialog), macOS (osascript), and
   * Windows (PowerShell). When no native dialog is available (e.g. ChromeOS),
   * the backend returns {fallback: true} and we use a hidden HTML file input.
   */
  const handlePickFiles = () => {
    // Only use the server-side OS dialog when we've CONFIRMED one exists (Linux
    // desktop / macOS / Windows). Otherwise — web, or ChromeOS Crostini (no
    // zenity) — click the hidden <input> SYNCHRONOUSLY: awaiting /api/files/pick
    // first and clicking only after the round-trip forfeits WebKit's user
    // activation and the file selection returns empty (no attachment pill,
    // 2026-07-21). Availability is probed at mount, so this stays synchronous.
    if (nativeDialogAvailable() === true) {
      void (async () => {
        try {
          const res = await fetch("/api/files/pick");
          const data = res.ok ? await res.json() : null;
          if (data?.paths?.length) {
            addFiles(await importPickedFiles(data.paths));
          } else if (data?.fallback) {
            fileInputRef.current?.click();
          }
        } catch (err) {
          console.error("File picker failed, falling back to HTML input:", err);
          fileInputRef.current?.click();
        }
      })();
      return;
    }
    fileInputRef.current?.click();
  };

  const handleSubmit = async () => {
    let message = text.trim();
    if (!message && attachedFiles.length === 0) return;

    if (attachedFiles.length > 0) {
      // All files have real filesystem paths (via native dialog)
      const pathFiles = attachedFiles.filter((f) => f.path);
      if (pathFiles.length > 0) {
        const fileSection =
          pathFiles.length === 1
            ? `\n\nAttached file: ${pathFiles[0]!.path}`
            : `\n\nAttached files:\n${pathFiles.map((f) => `- ${f.path}`).join("\n")}`;
        message = (message || "Please analyze the attached files") + fileSection;
      }
    }

    if (message) {
      sendMessage(message);
      setText("");
      clearFiles();
    }
  };

  /**
   * Insert a slash command into the textarea, replacing the user's partial
   * `/query`. When `andSubmit` is true (Enter inside the palette) we also
   * fire `handleSubmit` for commands that take no arguments — matching
   * Claude Code's "press Enter to run" behaviour.
   */
  const applySlashCommand = useCallback(
    (cmd: SlashCommand, andSubmit: boolean) => {
      // Trailing space so the user can immediately type an argument.
      const wantsArg = !!cmd.args;
      const inserted = wantsArg ? `/${cmd.name} ` : `/${cmd.name}`;
      setText(inserted);
      setSlashOpen(false);
      // Restore focus + caret position on the next tick (after the state
      // flush) so the textarea is ready for the user's next keystroke.
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.focus();
        el.setSelectionRange(inserted.length, inserted.length);
      });
      if (andSubmit && !wantsArg) {
        // Fire-and-forget — sendMessage is async but the queue handles it.
        void sendMessage(inserted);
        setText("");
        clearFiles();
      }
    },
    [sendMessage, clearFiles],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Slash-palette keyboard handling takes precedence over normal input.
    if (slashOpen && filteredCommands.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashIndex((i) => (i + 1) % filteredCommands.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashIndex((i) => (i - 1 + filteredCommands.length) % filteredCommands.length);
        return;
      }
      if (e.key === "Tab") {
        e.preventDefault();
        const cmd = filteredCommands[slashIndex];
        if (cmd) applySlashCommand(cmd, false);
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        const cmd = filteredCommands[slashIndex];
        if (cmd) applySlashCommand(cmd, true);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSlashOpen(false);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    // pb grows to clear the phone home-indicator / gesture bar (safe-area),
    // never less than the original 1rem.
    <div className="px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
      <DictationSetupModal
        open={setupOpen}
        deviceId={micDeviceId}
        onDeviceChange={persistDeviceId}
        onClose={() => setSetupOpen(false)}
        onReady={handleSetupDone}
      />
      <div className="max-w-3xl mx-auto">
        {/* Recap — "where this chat is at", above the composer so it's the last
            thing read before answering. Self-gating: renders nothing unless the
            last reply is long enough to be worth summarising. */}
        <RecapBar />
        {/* Attached files bar */}
        {attachedFiles.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {attachedFiles.map((file, i) => (
              <div
                key={`${file.name}-${i}`}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-jove-10 border border-jove-60/40 rounded-lg text-xs animate-[scaleIn_150ms_ease-out]"
              >
                <svg className="h-3.5 w-3.5 text-jove-80" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
                {/* ``code-ink``, not the fixed ``jove-200``: the 60/80/200/400 ink
                    shades don't re-theme, so #3610A2 on the dark ``bg-jove-10``
                    tint was near-black on near-black. This is the same token
                    inline `code` uses for exactly this reason — jove-200 in
                    light, a light lavender in dark. */}
                <span className="text-code-ink font-medium truncate max-w-[200px]" title={file.path}>{file.name}</span>
                <button
                  onClick={() => removeFile(i)}
                  className="text-space-40 hover:text-hydra transition-colors ml-0.5"
                >
                  <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/*
         * Anchor for the slash-command menu — it pops up with
         * `absolute bottom-full`, so this wrapper must be `relative`
         * AND sit directly above the input card with no other layout
         * in between, otherwise the menu won't line up.
         */}
        <div className="relative">
          {slashOpen && (
            <SlashCommandMenu
              commands={filteredCommands}
              activeIndex={slashIndex}
              onSelect={(cmd) => applySlashCommand(cmd, false)}
              onHoverIndex={setSlashIndex}
              query={slashQuery ?? ""}
              loading={catalogLoading}
              error={catalogError}
            />
          )}

        {/* Input card */}
        <div className="flex items-end gap-2 bg-surface rounded-2xl border border-space-30 shadow-sm
                        focus-within:border-jove-60 focus-within:ring-2 focus-within:ring-jove-60/20
                        transition-all duration-150 px-2 py-1.5">
          {/* Hidden file input for fallback (ChromeOS, etc.) */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileInputChange}
          />
          {/* "+" menu: add files + per-conversation connector toggles.
              Hidden on mobile once the user starts typing (kept on desktop) so
              the textarea gets the full width; reappears when the box is empty. */}
          <div className={`${text ? "hidden md:flex" : "flex"} items-end`}>
            <ComposerPlusMenu onAddFiles={handlePickFiles} />
          </div>

          {/*
           * Mic + chevron group. The main button toggles dictation (or
           * opens the setup modal on first use, when the recognizer model
           * isn't installed yet); the chevron always opens the modal so
           * the user can re-pick a device or reinstall the model.
           * Also mobile-hidden while typing, for the same room reason.
           */}
          <div className={`${text ? "hidden md:flex" : "flex"} items-stretch shrink-0`}>
            <button
              onClick={handleMicClick}
              className={`pl-2 pr-1 py-2 rounded-l-xl transition-colors ${
                isDictating
                  ? "bg-hydra text-white hover:bg-hydra-50 hover:text-hydra animate-pulse"
                  : "text-space-40 hover:text-jove hover:bg-jove-10"
              }`}
              title={
                dictation.status === "requesting"
                  ? "Waiting for microphone access…"
                  : dictation.status === "finalizing"
                    ? "Transcribing…"
                    : isDictating
                      ? "Stop & transcribe"
                      : modelReady === false
                        ? "Set up dictation"
                        : "Dictate (mic)"
              }
              aria-pressed={isDictating}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 11a7 7 0 01-14 0M12 18v4m-4 0h8" />
                <rect x="9" y="3" width="6" height="12" rx="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              onClick={handleChevronClick}
              className={`pr-1.5 pl-0.5 py-2 rounded-r-xl transition-colors ${
                isDictating
                  ? "bg-hydra text-white hover:bg-hydra-50 hover:text-hydra"
                  : "text-space-40 hover:text-jove hover:bg-jove-10"
              }`}
              title="Microphone & model settings"
              aria-label="Open dictation settings"
            >
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/*
           * Textarea + slash-command highlight overlay.
           *
           * The textarea renders its text normally (so native selection,
           * IME, and accessibility keep working). The overlay sits ON TOP
           * with `pointer-events: none` and re-renders the same content:
           * non-command tokens are transparent (kept purely for layout
           * alignment), command tokens render in jove purple with a faint
           * jove-tinted background that masks the textarea's plain text
           * underneath. Both elements must share font, padding,
           * line-height, and wrapping — keep their classNames in sync.
           */}
          <div className="relative flex-1 min-h-[40px]">
            {isDictating ? (
              // Replaces the textarea while the user is recording or the
              // backend is decoding. The textarea content (and any
              // already-typed message) is preserved in state and reappears
              // when the dictation flow exits — the appended transcript
              // lands at the cursor on top of whatever was there.
              <DictationOverlay
                levels={dictation.levels}
                elapsedMs={dictation.elapsedMs}
                finalizing={isFinalizing}
                onCancel={handleDictationCancel}
              />
            ) : (
              <>
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onScroll={(e) => {
                    if (overlayRef.current) {
                      overlayRef.current.scrollTop = e.currentTarget.scrollTop;
                    }
                  }}
                  placeholder={
                    hasMessages
                      ? "Ask a follow-up…"
                      : "How can I help you today?"
                  }
                  rows={1}
                  spellCheck={!text.startsWith("/")}
                  className="relative block w-full resize-none py-2 pr-1 bg-transparent text-sm leading-relaxed
                             focus:outline-none placeholder:text-space-40 max-h-[200px] font-sans text-space"
                />
                <div
                  ref={overlayRef}
                  aria-hidden="true"
                  className="absolute inset-0 py-2 pr-1 pointer-events-none overflow-hidden
                             whitespace-pre-wrap break-words text-sm leading-relaxed font-sans"
                >
                  {tokens.map((tok, i) =>
                    tok.type === "command" ? (
                      <span
                        key={i}
                        className="text-jove font-medium bg-jove-10 rounded-[3px] px-[1px] -mx-[1px]"
                      >
                        {tok.value}
                      </span>
                    ) : (
                      <span key={i} style={{ color: "transparent" }}>
                        {tok.value}
                      </span>
                    ),
                  )}
                  {/* Trailing ZWSP keeps the final newline from collapsing in the overlay. */}
                  {"​"}
                </div>
              </>
            )}
          </div>

          {/* Shortlist panel toggle */}
          {liveData && !rightPanelOpen && (
            <button
              onClick={() => openRightPanel("shortlist")}
              className="p-2 rounded-xl bg-jove-30 text-jove hover:bg-jove-50 transition-colors shrink-0"
              title="Show shortlist"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </button>
          )}

          {isAgentRunning && (
            <button
              onClick={interrupt}
              className="p-2 rounded-xl bg-hydra-50 text-hydra hover:bg-hydra hover:text-white transition-colors shrink-0"
              title="Stop agent"
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          )}

          {queuedCount > 0 && (
            <button
              onClick={() => clearQueuedMessage()}
              className="flex items-center gap-1 p-2 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors shrink-0 text-xs font-medium"
              title="Clear queued messages"
            >
              {queuedCount}
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          {(
            <button
              onClick={handleSubmit}
              disabled={!text.trim() && attachedFiles.length === 0}
              className="p-2 rounded-xl bg-primary text-on-primary hover:bg-jove-200 transition-colors
                         disabled:opacity-30 disabled:cursor-not-allowed shrink-0"
              title={isAgentRunning ? "Queue message" : "Send"}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          )}
        </div>
        </div>{/* close .relative slash-menu anchor */}

        {/* Bottom bar: model selector + token/cost/files widgets, kept on ONE
            line via a horizontal scroller (no scrollbar, edge fades) rather than
            wrapping to a second row — cleaner on a narrow screen. Any dictation
            notices sit on their own line below. */}
        <div className="mt-1.5 px-1">
          <HScroll>
            <ModelSelector />
            <EnvironmentSelector />
            {/* Beside the environment selector deliberately: the two answer the
                same question ("where is my work, and is any of it still
                moving?"), and outstanding runs are what blocks a move. */}
            <RunningTasksChip />
            <TokenCounter />
            <CostCounter />
            {artifacts.length > 0 && (
              <button
                onClick={() => openRightPanel("files")}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all duration-150
                  ${rightPanelOpen
                    ? "bg-primary text-on-primary"
                    : "bg-space-20 text-space-50 hover:bg-space-30 hover:text-space"
                  }`}
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                </svg>
                {artifacts.length} file{artifacts.length !== 1 ? "s" : ""}
              </button>
            )}
          </HScroll>
          {(dictation.error || (dictationTruncated && !isDictating)) && (
            <div className="flex items-center gap-3 mt-1">
              {dictation.error && (
                <p className="text-[10px] text-hydra max-w-[260px] truncate" title={dictation.error}>
                  Mic: {dictation.error}
                </p>
              )}
              {dictationTruncated && !isDictating && (
                <p
                  className="text-[10px] text-amber-600 max-w-[260px] truncate"
                  title="Dictation hit the 2-minute cap — the tail of your speech was clipped."
                >
                  Dictation was clipped at 2 min
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
